import config from "@/app.config";
import routes from "@/app.route";
import {
  PAY_LATER_AMOUNT,
  PAY_LATER_MIN_VALUE,
  PAY_LATER_PERCENTAGE,
  PAYMENT_METHOD,
} from "@/constants/payment-method.constant";
import { getMessage, getYupErrorMessages } from "@/lib/error";
import { VariantType } from "@/types/product.type";
import axios from "axios";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as yup from "yup";
import { getWithGstAmount } from "@/lib/gst";

const schema = yup.object({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  phone: yup
    .string()
    .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits")
    .required("Phone number is required"),
  shipping_first_name: yup
    .string()
    .min(2, "First name must be at least 2 characters")
    .required("First name is required"),
  shipping_last_name: yup
    .string()
    .min(2, "Last name must be at least 2 characters")
    .required("Last name is required"),
  shipping_address1: yup.string().required("Address line 1 is required"),
  shipping_address2: yup.string().required("Address line 2 is required"),
  shipping_city: yup.string().required("City is required"),
  shipping_province: yup.string().required("Please select a province/state"),
  shipping_zip: yup
    .string()
    .matches(/^[0-9]{6}$/, "ZIP / Postal code must be exactly 6 digits")
    .required("ZIP / Postal code is required"),
});

type Props = {
  dataLoading: boolean;
  loading: boolean;
  error: string;
  errors: Record<string, string>;
  contact: {
    email: string;
    phone: string;
  };
  shipping: {
    shipping_first_name: string;
    shipping_last_name: string;
    shipping_address1: string;
    shipping_address2: string;
    shipping_city: string;
    shipping_province: string;
    shipping_zip: string;
    shipping_company: string;
    shipping_country: string;
    shipping_phone: string;
  };
  items: (VariantType & { quantity: string })[];
  payment_method: string;
  payment_gateway: string;
  options: {
    subtotal_price: string | number;
    total_price: string | number;
    total_outstanding: string | number;
    pay_later_amount: string | number;
    pay_later_charges: string | number;
  };
  hydrated: boolean;
  setHydrated: (value: boolean) => void;
  recalculate: () => void;
  setOptions: (key: string, value: string | number) => void;
  clearCart: () => void;
  setContact: (key: string, value: string) => void;
  setShipping: (key: string, value: string) => void;
  setPaymentGateway: (value: string) => void;
  setPaymentMethod: (value: string) => void;
  setDataLoading: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  setError: (value: string) => void;
  setErrors: (value: Record<string, string>) => void;
  fetchProducts: () => Promise<void>;
  addItem: (item: VariantType & { quantity: string }) => void;
  removeItem: (id: string) => void;
  increment: (id: string) => void;
  decrement: (id: string) => void;
  checkout: () => Promise<void>;
  revalidate: (key: string, value: string) => Promise<void>;
};

const useCart = create<Props>()(
  persist(
    (set, get) => ({
      dataLoading: false,
      loading: false,
      error: "",
      errors: {},
      contact: {
        email: "",
        phone: "",
      },
      shipping: {
        shipping_first_name: "",
        shipping_last_name: "",
        shipping_address1: "",
        shipping_address2: "",
        shipping_city: "",
        shipping_province: "",
        shipping_zip: "",
        shipping_company: "",
        shipping_country: "India",
        shipping_phone: "",
      },
      items: [],
      payment_method: PAYMENT_METHOD.ONLINE,
      payment_gateway: "payu",
      options: {
        subtotal_price: 0,
        total_price: 0,
        total_outstanding: 0,
        pay_later_amount: 0,
        pay_later_charges: 0,
      },
      hydrated: false,
      setHydrated: (v) => {
        const { setError, setErrors, recalculate } = get();

        set({ hydrated: v });

        recalculate();
        setError("");
        setErrors({});
      },
      recalculate: () => {
        const { payment_method, items, setOptions } = get();

        const subtotalPrice = items.reduce(
          (acc, i) =>
            acc + getWithGstAmount(i.price, i.gst_rate) * Number(i.quantity),
          0,
        );

        const subtotalWithoutGstPrice = items.reduce(
          (acc, i) => acc + Number(i.price) * Number(i.quantity),
          0,
        );

        let totalOutstanding = subtotalPrice;
        let payLaterAmount = "0";
        let payLaterCharges = "0";
        let totalPrice = "0";

        if (
          payment_method == PAYMENT_METHOD.PAY_LATER &&
          totalOutstanding > PAY_LATER_MIN_VALUE
        ) {
          payLaterAmount = PAY_LATER_AMOUNT.toFixed(2);
          payLaterCharges = (
            PAY_LATER_PERCENTAGE *
            (subtotalWithoutGstPrice - Number(payLaterAmount))
          ).toFixed(2);
          totalOutstanding = subtotalPrice - Number(payLaterCharges);
        }

        totalPrice = (Number(subtotalPrice) + Number(payLaterCharges)).toFixed(
          2,
        );

        setOptions("subtotal_price", subtotalPrice);
        setOptions("total_price", totalPrice);
        setOptions("total_outstanding", totalOutstanding);
        setOptions("pay_later_amount", payLaterAmount);
        setOptions("pay_later_charges", payLaterCharges);
      },
      setOptions: (key, value) => {
        const { options } = get();

        set({ options: { ...options, [key]: value } });
      },
      clearCart: () => {
        set({
          contact: {
            email: "",
            phone: "",
          },
          shipping: {
            shipping_first_name: "",
            shipping_last_name: "",
            shipping_address1: "",
            shipping_address2: "",
            shipping_city: "",
            shipping_province: "",
            shipping_zip: "",
            shipping_company: "",
            shipping_country: "India",
            shipping_phone: "",
          },
          items: [],
          payment_method: PAYMENT_METHOD.ONLINE,
          payment_gateway: "payu",
          options: {
            subtotal_price: 0,
            total_price: 0,
            total_outstanding: 0,
            pay_later_amount: 0,
            pay_later_charges: 0,
          },
        });
      },
      setContact: (key, value) => {
        const { contact, revalidate } = get();

        set({ contact: { ...contact, [key]: value } });

        revalidate(key, value);
      },
      setShipping: (key, value) => {
        const { shipping, revalidate } = get();

        set({ shipping: { ...shipping, [key]: value } });

        revalidate(key, value);
      },
      setPaymentGateway: (value) => set({ payment_gateway: value }),
      setPaymentMethod: (value) => set({ payment_method: value }),
      setLoading: (value) => set({ loading: value }),
      setDataLoading: (value) => set({ dataLoading: value }),
      setError: (value) => set({ error: value }),
      setErrors: (value) => set({ errors: value }),
      fetchProducts: async () => {
        const { items, setDataLoading, setError } = get();
        const id = items.map((v) => v.id);

        try {
          setDataLoading(true);

          const response = await axios.get(
            `${config.app.apiBaseUrl}/api/v2/products`,
            {
              params: {
                id,
              },
            },
          );

          const itemMap = Object.fromEntries(
            items.map((item) => [item.id, item.quantity]),
          );

          const products = response.data.data.map((p: VariantType) => ({
            ...p,
            quantity: itemMap[p.id],
          }));

          set({ items: products });
        } catch (error: unknown) {
          setError(getMessage(error));
        } finally {
          setDataLoading(false);
        }
      },
      addItem: (item) => {
        const { recalculate, items } = get();

        const existingItemIndex = items.findIndex((i) => i.id == item.id);

        let updatedItems;

        if (existingItemIndex !== -1) {
          updatedItems = items.map((i, index) =>
            index === existingItemIndex
              ? { ...i, quantity: String(Number(i.quantity) + 1) }
              : i,
          );
        } else {
          updatedItems = [...items, { ...item, quantity: "1" }];
        }

        set({ items: updatedItems });

        recalculate();
      },
      removeItem: (id) => {
        const { recalculate, items } = get();

        set({ items: items.filter((v) => v.id != id) });

        recalculate();
      },
      increment: (id) => {
        const { recalculate, items } = get();

        const newItems = items.map((i) =>
          i.id == id ? { ...i, quantity: String(Number(i.quantity) + 1) } : i,
        );

        set({ items: newItems });

        recalculate();
      },
      decrement: (id) => {
        const { recalculate, items } = get();

        const newItems = items
          .map((i) =>
            i.id == id ? { ...i, quantity: String(Number(i.quantity) - 1) } : i,
          )
          .filter((i) => Number(i.quantity) > 0);

        set({ items: newItems });

        recalculate();
      },
      checkout: async () => {
        const {
          contact,
          shipping,
          items,
          payment_method,
          payment_gateway,
          setLoading,
          setError,
          setErrors,
        } = get();

        try {
          setLoading(true);
          setErrors({});

          await schema.validate(
            {
              ...contact,
              ...shipping,
            },
            { abortEarly: false },
          );

          const response = await axios.post(
            `${config.app.apiBaseUrl}/api/v2/checkout`,
            {
              ...contact,
              ...shipping,
              payment_method,
              payment_gateway,
              items,
            },
            {
              headers: {
                Accept: "application/json",
              },
              withCredentials: true,
            },
          );

          if (response.data.error) {
            setError(response.data.message);
            return;
          }

          if (payment_gateway == "razorpay") {
            const options = {
              key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID!,
              amount:
                process.env.NODE_ENV !== "production"
                  ? 100
                  : response.data.data.amount * 100,
              currency: "INR",
              name: config.app.name,
              description: "Razorpay Transaction",
              image: "/branding/logo.png",
              ...(process.env.NODE_ENV == "production" && {
                order_id: `${response.data.data.id}`,
              }),
              handler: async function (payload: {
                razorpay_payment_id: string;
              }) {
                const res = await axios.post(
                  `${config.app.apiBaseUrl}/api/v2/checkout/${response.data.data.id}/payment`,
                  {
                    ...payload,
                  },
                  {
                    headers: {
                      Accept: "application/json",
                    },
                    withCredentials: true,
                  },
                );

                if (res.data.error) {
                  setError(res.data.message);
                  return;
                }

                window.location.href = `${routes.userOrder.url.replace(":id", response.data.data.id)}?success=true`;
              },
              prefill: {
                name: `${shipping.shipping_first_name} ${shipping.shipping_last_name}`,
                email: contact.email,
                contact: contact.phone,
              },
              theme: {
                color: "#4687c7",
              },
            };

            const razorpay = new (window as any).Razorpay(options);

            razorpay.open();
          } else if (payment_gateway == "phonepe") {
            window.location.href = response.data.data.url;
          } else if (payment_gateway == "payu") {
            const res = await axios.post("/api/pg/payu/init", {
              id: response.data.data.id,
            });

            const form = document.createElement("form");
            form.method = "POST";
            form.action = `${config.service.payu.baseUrl}/_payment`;
            Object.entries(res.data.data).forEach(([key, value]) => {
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = key;
              input.value = `${value}`;
              form.appendChild(input);
            });
            document.body.appendChild(form);
            form.submit();
          } else {
            window.location.href = `${routes.userOrder.url.replace(":id", response.data.data.id)}?success=true`;
          }
        } catch (error: unknown) {
          const errors = getYupErrorMessages(error);

          if (errors) {
            setErrors(errors);
          } else {
            setError(getMessage(error));
          }
        } finally {
          setLoading(false);
        }
      },
      revalidate: async (key, value) => {
        const { contact, shipping, errors, setErrors } = get();

        try {
          await schema.validateAt(key, {
            ...contact,
            ...shipping,
            [key]: value,
          });

          const updatedErrors = { ...errors };

          delete updatedErrors[key];

          setErrors(updatedErrors);
        } catch (error: unknown) {
          const errors = getYupErrorMessages(error);

          if (errors) {
            setErrors(errors);
          }
        }
      },
    }),
    {
      name: "cart",
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHydrated(true);
        }
      },
    },
  ),
);

export default useCart;
